import React, { useState, useEffect, useRef } from 'react';
import { Thermometer, Snowflake, Refrigerator, Zap, Droplets, Wrench } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

// Import modular components
import { 
  HeroSection, 
  HowItWorksSection, 
  ReviewsSection, 
  UstaJoinSection, 
  GuaranteeSection,
  ServicesSection,
  PopularCitiesSection
} from '../components/home';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Schema.org Structured Data for HomePage
const homePageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://hizmetgeliyo.com/#webpage",
  "url": "https://hizmetgeliyo.com",
  "name": "HizmetGeliyo - 2 Dakikada Güvenilir Usta Bul",
  "description": "Türkiye'nin en güvenilir ev hizmetleri platformu. Kombi, klima, beyaz eşya, elektrik ve tesisat hizmetleri.",
  "isPartOf": { "@id": "https://hizmetgeliyo.com/#website" },
  "about": { "@id": "https://hizmetgeliyo.com/#organization" },
  "mainEntity": {
    "@type": "ItemList",
    "name": "Ev Hizmetleri",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "Kombi Servisi",
          "description": "Kombi arıza, bakım ve montaj hizmetleri",
          "url": "https://hizmetgeliyo.com/kombi-servisi",
          "provider": { "@id": "https://hizmetgeliyo.com/#organization" }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Klima Servisi",
          "description": "Klima montaj, bakım ve arıza hizmetleri",
          "url": "https://hizmetgeliyo.com/klima-servisi",
          "provider": { "@id": "https://hizmetgeliyo.com/#organization" }
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Service",
          "name": "Beyaz Eşya Servisi",
          "description": "Çamaşır makinesi, buzdolabı, bulaşık makinesi tamiri",
          "url": "https://hizmetgeliyo.com/beyaz-esya-servisi",
          "provider": { "@id": "https://hizmetgeliyo.com/#organization" }
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Service",
          "name": "Elektrik Servisi",
          "description": "Elektrik arıza ve tesisat hizmetleri",
          "url": "https://hizmetgeliyo.com/elektrik-servisi",
          "provider": { "@id": "https://hizmetgeliyo.com/#organization" }
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "Service",
          "name": "Su Tesisatı",
          "description": "Su tesisatı, tıkanıklık açma, kaçak tespiti",
          "url": "https://hizmetgeliyo.com/talep/su_tesisati",
          "provider": { "@id": "https://hizmetgeliyo.com/#organization" }
        }
      }
    ]
  }
};

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Arama önerileri
  const searchSuggestions = [
    { id: 'kombi', name: 'Kombi Servisi', icon: Thermometer, keywords: ['kombi', 'kalorifer', 'ısıtma', 'doğalgaz'] },
    { id: 'klima', name: 'Klima Servisi', icon: Snowflake, keywords: ['klima', 'soğutma', 'split', 'inverter'] },
    { id: 'beyaz_esya', name: 'Beyaz Eşya Tamiri', icon: Refrigerator, keywords: ['buzdolabı', 'çamaşır', 'bulaşık', 'fırın', 'beyaz eşya'] },
    { id: 'elektrik', name: 'Elektrik Servisi', icon: Zap, keywords: ['elektrik', 'sigorta', 'priz', 'aydınlatma'] },
    { id: 'su_tesisati', name: 'Su Tesisatı', icon: Droplets, keywords: ['su', 'tesisat', 'tıkanıklık', 'kaçak', 'musluk'] },
    { id: 'genel', name: 'Diğer Hizmetler', icon: Wrench, keywords: ['tamir', 'montaj', 'bakım'] },
  ];

  // Müşteri yorumları
  const [customerReviews, setCustomerReviews] = useState([
    { id: 1, name: 'Yükleniyor...', location: '', rating: 5, comment: 'Yorumlar yükleniyor...', service: '', avatar: '...' }
  ]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  // Filtrelenmiş öneriler
  const filteredSuggestions = searchQuery.trim() 
    ? searchSuggestions.filter(s => 
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : searchSuggestions;

  // AI Yorumları çek
  const fetchAIReviews = async () => {
    try {
      setReviewsLoading(true);
      const response = await axios.get(`${API_URL}/api/ai-reviews`);
      if (response.data.success) {
        setCustomerReviews(response.data.reviews);
      }
    } catch (error) {
      console.error('Yorumlar yüklenemedi:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    fetchAIReviews();
    
    // Her 30 saniyede yorumları yenile
    const reviewsInterval = setInterval(() => {
      fetchAIReviews();
    }, 30000);
    
    // Click outside to close suggestions
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(reviewsInterval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homePageSchema) }} />
      
      <Navbar />

      {/* Hero Section */}
      <HeroSection 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        filteredSuggestions={filteredSuggestions}
        searchRef={searchRef}
      />

      {/* Nasıl Çalışır */}
      <HowItWorksSection />

      {/* Müşteri Yorumları */}
      <ReviewsSection 
        customerReviews={customerReviews}
        reviewsLoading={reviewsLoading}
      />

      {/* Usta Katılım */}
      <UstaJoinSection />

      {/* Garanti Bölümü */}
      <GuaranteeSection />

      {/* Hizmetler / SEO */}
      <ServicesSection />

      {/* Popüler Şehirler */}
      <PopularCitiesSection />

      <Footer />
    </div>
  );
};

export default HomePage;
